import { ChangeDetectionStrategy, Component, computed, input, output, signal, ViewEncapsulation } from '@angular/core';
import { ExamdojoAgGridModule } from '@examdojo/ag-grid';
import { IHeaderAngularComp } from 'ag-grid-angular';
import { ColDef, GridOptions, ICellRendererParams, IHeaderParams } from 'ag-grid-community';
import katex from 'katex';

import { OutcomeSvgComponent } from './outcome-svg.component';
import { ExperimentTrialResult, ProbabilityExperimentDefinition } from './probability-experiment.model';

interface FrequencyRow {
  readonly label: string;
  readonly labelHtml: string;
  [key: string]: string | number;
}

function renderLatex(latex: string): string {
  return katex.renderToString(latex, { throwOnError: false, output: 'html' });
}

// ── Custom header showing the outcome SVG icon ──────────────────────
export interface OutcomeHeaderParams {
  readonly svgType: string;
}

@Component({
  selector: 'dojo-outcome-header',
  standalone: true,
  imports: [OutcomeSvgComponent],
  template: `<dojo-outcome-svg [svgType]="svgType()" />`,
  styles: `
    dojo-outcome-header {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 100%;
      height: 100%;

      dojo-outcome-svg {
        width: 32px;
        height: 32px;
      }
    }
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
})
export class OutcomeHeaderComponent implements IHeaderAngularComp {
  readonly svgType = signal('');

  agInit(params: IHeaderParams & OutcomeHeaderParams): void {
    this.svgType.set(params.svgType);
  }

  refresh(params: IHeaderParams & OutcomeHeaderParams): boolean {
    this.svgType.set(params.svgType);
    return true;
  }
}

// ── Frequency table component ───────────────────────────────────────

const LABEL_RESULT = 'result';
const LABEL_COUNT = 'count';
const LABEL_PROB = 'prob';
const VALUE_SUCCESS = 'Success';
const VALUE_LOSS = 'Loss';

const LABEL_HTML: Record<string, string> = {
  [LABEL_RESULT]: renderLatex('\\text{Result}'),
  [LABEL_COUNT]: renderLatex('\\text{Count}'),
  [LABEL_PROB]: renderLatex('P(X\\!=\\!x)'),
};

@Component({
  selector: 'dojo-experiment-frequency-table',
  standalone: true,
  imports: [ExamdojoAgGridModule],
  templateUrl: './experiment-frequency-table.component.html',
  styleUrl: './experiment-frequency-table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  encapsulation: ViewEncapsulation.None,
  host: { class: 'block' },
})
export class ExperimentFrequencyTableComponent {
  private static readonly LABEL_COL_WIDTH = 100;
  private static readonly OUTCOME_COL_WIDTH = 70;
  private static readonly GRID_BORDER = 2;

  readonly results = input.required<ExperimentTrialResult[]>();
  readonly experiment = input.required<ProbabilityExperimentDefinition>();
  readonly targetOutcomes = input.required<boolean[]>();
  readonly highlightChange = output<'success' | 'loss'>();

  readonly columnDefs = computed<Array<ColDef<FrequencyRow>>>(() => {
    const exp = this.experiment();

    const labelCol: ColDef<FrequencyRow> = {
      field: 'label',
      headerName: '',
      pinned: 'left',
      width: 100,
      maxWidth: 100,
      suppressMovable: true,
      suppressSizeToFit: true,
      cellClass: 'frequency-label-cell',
      cellRenderer: (params: ICellRendererParams<FrequencyRow>) => params.data?.labelHtml ?? '',
    };

    const selected = this.targetOutcomes();
    const outcomeCols: Array<ColDef<FrequencyRow>> = exp.outcomes.map((outcome, i) => ({
      field: `o_${i}`,
      headerName: '',
      headerComponent: OutcomeHeaderComponent,
      headerComponentParams: { svgType: outcome.svgType } satisfies OutcomeHeaderParams,
      headerClass: selected[i] ? 'frequency-header--success' : 'frequency-header--loss',
      width: 70,
      maxWidth: 80,
      suppressMovable: true,
      suppressSizeToFit: true,
      enableCellChangeFlash: true,
      cellClass: ['frequency-value-cell', selected[i] ? 'frequency-col--success' : 'frequency-col--loss'],
      cellClassRules: {
        'frequency-cell--success': (params) => params.data?.label === LABEL_RESULT && params.value === VALUE_SUCCESS,
        'frequency-cell--loss': (params) => params.data?.label === LABEL_RESULT && params.value === VALUE_LOSS,
      },
      cellRenderer: (params: ICellRendererParams<FrequencyRow>) => {
        const value = params.value;
        if (value === null || value === undefined) {
          return '';
        }
        if (params.data?.label === LABEL_RESULT) {
          return String(value);
        }
        if (params.data?.label === LABEL_COUNT) {
          return renderLatex(String(value));
        }
        return renderLatex((value as number).toFixed(2));
      },
    }));

    const lastIdx = exp.outcomes.length - 1;
    if (outcomeCols[lastIdx]) {
      outcomeCols[lastIdx].headerClass = 'frequency-last-header';
      outcomeCols[lastIdx].cellStyle = { borderRight: 'none' };
    }

    return [labelCol, ...outcomeCols];
  });

  readonly rowData = computed<FrequencyRow[]>(() => {
    const exp = this.experiment();
    const trials = this.results();
    const total = trials.length;
    const selected = this.targetOutcomes();

    const counts = new Array<number>(exp.outcomes.length).fill(0);
    for (const trial of trials) {
      const idx = exp.outcomes.indexOf(trial.outcome);
      if (idx >= 0) {
        counts[idx]++;
      }
    }

    const resultRow: FrequencyRow = { label: LABEL_RESULT, labelHtml: LABEL_HTML[LABEL_RESULT] };
    const countRow: FrequencyRow = { label: LABEL_COUNT, labelHtml: LABEL_HTML[LABEL_COUNT] };
    const probRow: FrequencyRow = { label: LABEL_PROB, labelHtml: LABEL_HTML[LABEL_PROB] };

    for (let i = 0; i < exp.outcomes.length; i++) {
      const key = `o_${i}`;
      const c = counts[i];
      const p = total > 0 ? c / total : 0;
      resultRow[key] = selected[i] ? VALUE_SUCCESS : VALUE_LOSS;
      countRow[key] = c;
      probRow[key] = p;
    }

    return [resultRow, countRow, probRow];
  });

  readonly gridWidth = computed(() => {
    const outcomeCount = this.experiment().outcomes.length;
    return (
      ExperimentFrequencyTableComponent.LABEL_COL_WIDTH +
      outcomeCount * ExperimentFrequencyTableComponent.OUTCOME_COL_WIDTH +
      ExperimentFrequencyTableComponent.GRID_BORDER
    );
  });

  readonly gridOptions: GridOptions<FrequencyRow> = {
    domLayout: 'autoHeight',
    headerHeight: 40,
    rowHeight: 32,
    suppressRowHoverHighlight: true,
    suppressCellFocus: true,
    suppressContextMenu: true,
    getRowId: (params) => params.data.label,
    onCellMouseOver: (event) => {
      const field = event.column?.getColId();
      if (field?.startsWith('o_')) {
        const idx = parseInt(field.substring(2), 10);
        const selected = this.targetOutcomes();
        this.highlightChange.emit(selected[idx] ? 'success' : 'loss');
      }
    },
  };
}
